import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';

/**
 * SendToTVModal — shared between SlideManager and SlideBuilder (brand mode)
 *
 * Props:
 *   slides    – array of slide objects (need .id and optional .send_targets)
 *   slideIds  – array of slide IDs to send
 *   brandId   – brand ID (number or string)
 *   onClose   – called when modal should close
 *   onSent    – (slideId, send_targets) called after each slide is sent successfully
 *   endpoint  – API collection to POST /:id/send against. Defaults to the menu
 *               board's 'brand-slides'; the Door Sign page passes 'door-signs',
 *               whose rows carry the same id / send_targets shape.
 */
export default function SendToTVModal({ slides, slideIds, brandId, onClose, onSent, endpoint = 'brand-slides' }) {
  const [devices,        setDevices]        = useState([]);
  const [groups,         setGroups]         = useState([]);
  const [selDevices,     setSelDevices]     = useState({});
  const [selGroups,      setSelGroups]      = useState({});
  const [targetFolder,   setTargetFolder]   = useState('');
  const [subfolders,     setSubfolders]     = useState([]);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [sending,        setSending]        = useState(false);
  const [results,        setResults]        = useState(null);
  const [loadingTvs,     setLoadingTvs]     = useState(true);
  const [progress,       setProgress]       = useState({ current: 0, total: 0 });

  const firstSlide  = slides.find(s => slideIds.includes(s.id));
  const lastTargets = firstSlide?.send_targets || null;

  useEffect(() => {
    setLoadingTvs(true);
    // Pass brand_id so the backend returns the brand-owner's devices/groups
    // (relevant when an admin operates on a brand they don't own — without
    // this, the modal would list the admin's own TVs instead of the user's).
    const q = brandId ? `?brand_id=${encodeURIComponent(brandId)}` : '';
    Promise.all([
      api.get(`/users/my-devices${q}`),
      api.get(`/users/my-groups${q}`),
    ]).then(([dRes, gRes]) => {
      const devs = dRes.data || [];
      const grps = gRes.data || [];
      setDevices(devs);
      setGroups(grps);

      // Pre-fill from last send_targets
      if (lastTargets) {
        const devSel = {};
        (lastTargets.device_names || []).forEach(n => { devSel[n] = true; });
        setSelDevices(devSel);
        const grpSel = {};
        (lastTargets.group_ids || []).forEach(id => { grpSel[id] = true; });
        setSelGroups(grpSel);
        if (lastTargets.target_folder !== undefined) setTargetFolder(lastTargets.target_folder || '');
      }

      // Load subfolders from first device
      const probe = devs[0]?.device_name;
      if (probe) {
        setLoadingFolders(true);
        const probeQ = brandId
          ? `?device_name=${encodeURIComponent(probe)}&brand_id=${encodeURIComponent(brandId)}`
          : `?device_name=${encodeURIComponent(probe)}`;
        api.get(`/brand-slides/device-subfolders${probeQ}`)
          .then(r => setSubfolders(r.data || []))
          .catch(() => setSubfolders([]))
          .finally(() => setLoadingFolders(false));
      }
    }).catch(() => toast.error('Failed to load TVs')).finally(() => setLoadingTvs(false));
  }, []); // eslint-disable-line

  const selDeviceNames = Object.keys(selDevices).filter(k => selDevices[k]);
  const selGroupIds    = Object.keys(selGroups).filter(k => selGroups[k]).map(Number);
  const hasSelection   = selDeviceNames.length > 0 || selGroupIds.length > 0;

  const toggleAllDevices = () => {
    const allOn = devices.every(d => selDevices[d.device_name]);
    const next  = {};
    devices.forEach(d => { next[d.device_name] = !allOn; });
    setSelDevices(next);
  };
  const toggleAllGroups = () => {
    const allOn = groups.every(g => selGroups[g.id]);
    const next  = {};
    groups.forEach(g => { next[g.id] = !allOn; });
    setSelGroups(next);
  };

  const handleSend = async () => {
    if (!hasSelection) { toast.error('Select at least one TV or group'); return; }
    setSending(true);
    setResults(null);
    setProgress({ current: 0, total: slideIds.length });
    let totalSent = 0, totalFailed = 0, allErrors = [];

    for (let i = 0; i < slideIds.length; i++) {
      setProgress({ current: i + 1, total: slideIds.length });
      try {
        const res = await api.post(`/${endpoint}/${slideIds[i]}/send`, {
          brand_id:      parseInt(brandId),
          device_names:  selDeviceNames,
          group_ids:     selGroupIds,
          target_folder: targetFolder,
        });
        totalSent   += res.data.success || 0;
        totalFailed += res.data.failed  || 0;
        allErrors    = [...allErrors, ...(res.data.errors || [])];
        if (onSent) onSent(slideIds[i], res.data.send_targets);
      } catch (e) {
        totalFailed++;
        allErrors.push(`Slide ${slideIds[i]}: ${e.response?.data?.error || e.message}`);
      }
    }

    setSending(false);
    setResults({ sent: totalSent, failed: totalFailed, errors: allErrors });
  };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ background: '#fff', borderRadius: 16, width: 460, maxWidth: '100%', maxHeight: '88vh', display: 'flex', flexDirection: 'column', boxShadow: '0 12px 48px rgba(0,0,0,0.22)' }}>

        {/* Header */}
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid #F0F0F0', display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 20 }}>📺</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 15, fontWeight: 700 }}>Send to TVs</div>
            <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>
              {slideIds.length} slide{slideIds.length > 1 ? 's' : ''}
              {lastTargets && <span style={{ marginLeft: 6, color: '#1565C0' }}>• last targets pre-filled</span>}
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 18, cursor: 'pointer', color: '#999', padding: 4 }}>✕</button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
          {loadingTvs ? (
            <div style={{ textAlign: 'center', padding: 32, color: '#aaa' }}>Loading TVs…</div>
          ) : results ? (
            <div style={{ padding: '8px 0' }}>
              <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>
                {results.failed === 0 ? '✅' : '⚠️'} Send complete
              </div>
              {results.sent > 0 && (
                <div style={{ color: '#2E7D32', fontSize: 13, marginBottom: 6 }}>
                  ✓ Sent to {results.sent} TV{results.sent > 1 ? 's' : ''} successfully
                </div>
              )}
              {results.failed > 0 && (
                <div style={{ color: '#C62828', fontSize: 13, marginBottom: 6 }}>
                  ✗ Failed: {results.failed} TV{results.failed > 1 ? 's' : ''}
                </div>
              )}
              {results.errors.length > 0 && (
                <div style={{ marginTop: 8, maxHeight: 120, overflowY: 'auto', background: '#FFF5F5', borderRadius: 8, padding: '8px 10px' }}>
                  {results.errors.map((e, i) => (
                    <div key={i} style={{ fontSize: 11, color: '#C62828', marginBottom: 3 }}>• {e}</div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <>
              {/* Destination folder */}
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginBottom: 6 }}>
                  Destination folder
                  <span style={{ fontWeight: 400, color: '#999', marginLeft: 6 }}>(leave empty for device root)</span>
                </div>
                <input
                  value={targetFolder}
                  onChange={e => setTargetFolder(e.target.value)}
                  placeholder="Root of device"
                  style={{ width: '100%', padding: '7px 10px', borderRadius: 8, border: '1.5px solid #E0E0E0', fontSize: 13, boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' }}
                />
                {loadingFolders ? (
                  <div style={{ fontSize: 11, color: '#aaa', marginTop: 6 }}>Loading folders…</div>
                ) : subfolders.length > 0 ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginTop: 8 }}>
                    <button onClick={() => setTargetFolder('')}
                      style={{ padding: '3px 10px', borderRadius: 20, border: '1.5px solid', borderColor: targetFolder === '' ? '#1565C0' : '#E0E0E0', background: targetFolder === '' ? '#E3F2FD' : '#F8F9FA', color: targetFolder === '' ? '#1565C0' : '#666', fontSize: 12, cursor: 'pointer', fontWeight: targetFolder === '' ? 700 : 400 }}>
                      📁 Root
                    </button>
                    {subfolders.map(f => (
                      <button key={f} onClick={() => setTargetFolder(f)}
                        style={{ padding: '3px 10px', borderRadius: 20, border: '1.5px solid', borderColor: targetFolder === f ? '#1565C0' : '#E0E0E0', background: targetFolder === f ? '#E3F2FD' : '#F8F9FA', color: targetFolder === f ? '#1565C0' : '#555', fontSize: 12, cursor: 'pointer', fontWeight: targetFolder === f ? 700 : 400 }}>
                        📂 {f}
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>

              {/* Groups */}
              {groups.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Groups</div>
                    <button onClick={toggleAllGroups} style={{ fontSize: 11, color: '#1565C0', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                      {groups.every(g => selGroups[g.id]) ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {groups.map(g => (
                      <label key={g.id} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', background: selGroups[g.id] ? '#E3F2FD' : '#F8F9FA', border: `1.5px solid ${selGroups[g.id] ? '#90CAF9' : '#E8E8E8'}`, transition: 'all 0.1s' }}>
                        <input type="checkbox" checked={!!selGroups[g.id]} onChange={e => setSelGroups(p => ({ ...p, [g.id]: e.target.checked }))} style={{ accentColor: '#1565C0', width: 15, height: 15, flexShrink: 0 }} />
                        <span style={{ width: 10, height: 10, borderRadius: 3, background: g.color || '#2196F3', flexShrink: 0 }} />
                        <span style={{ fontSize: 13, fontWeight: 500, flex: 1 }}>{g.name}</span>
                        <span style={{ fontSize: 11, color: '#999' }}>{(g.device_names || []).length} TVs</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Individual TVs */}
              {devices.length > 0 && (
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 7 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: '#555' }}>Individual TVs</div>
                    <button onClick={toggleAllDevices} style={{ fontSize: 11, color: '#1565C0', background: 'none', border: 'none', cursor: 'pointer', padding: 0, fontWeight: 600 }}>
                      {devices.every(d => selDevices[d.device_name]) ? 'Deselect all' : 'Select all'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {devices.map(d => (
                      <label key={d.device_name} style={{ display: 'flex', alignItems: 'center', gap: 9, padding: '6px 10px', borderRadius: 8, cursor: 'pointer', background: selDevices[d.device_name] ? '#E3F2FD' : '#F8F9FA', border: `1.5px solid ${selDevices[d.device_name] ? '#90CAF9' : '#E8E8E8'}`, transition: 'all 0.1s' }}>
                        <input type="checkbox" checked={!!selDevices[d.device_name]} onChange={e => setSelDevices(p => ({ ...p, [d.device_name]: e.target.checked }))} style={{ accentColor: '#1565C0', width: 15, height: 15, flexShrink: 0 }} />
                        <span style={{ width: 8, height: 8, borderRadius: '50%', background: d.is_online ? '#4CAF50' : '#ccc', flexShrink: 0 }} />
                        <span style={{ fontSize: 13, flex: 1 }}>{d.device_name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {devices.length === 0 && groups.length === 0 && (
                <div style={{ textAlign: 'center', padding: 24, color: '#aaa', fontSize: 13 }}>No TVs or groups found for your account.</div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid #F0F0F0', display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          {sending && (
            <span style={{ fontSize: 12, color: '#888', marginRight: 'auto' }}>
              Sending {progress.current}/{progress.total}…
            </span>
          )}
          {results ? (
            <button onClick={onClose} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: '#2196F3', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>Done</button>
          ) : (
            <>
              <button onClick={onClose} disabled={sending} style={{ padding: '8px 18px', borderRadius: 8, border: '1.5px solid #E0E0E0', background: '#fff', cursor: 'pointer', fontSize: 13, color: '#555' }}>Cancel</button>
              <button onClick={handleSend} disabled={sending || !hasSelection || loadingTvs}
                style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: hasSelection ? '#1565C0' : '#E0E0E0', color: hasSelection ? '#fff' : '#aaa', cursor: hasSelection ? 'pointer' : 'not-allowed', fontSize: 13, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 7 }}>
                {sending ? '⏳ Sending…' : '📺 Send Now'}
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
